import PatientPresenter from './PatientPresenter';

class DrugInteractionsPresenter {
  constructor(interactionData) {
    // Extract interactions from the gen_interactions field
    const genInteractions = interactionData && interactionData.gen_interactions ? interactionData.gen_interactions : [];
    
    // Extract medications from the medications field
    const medicationsString = interactionData && interactionData.medications ? interactionData.medications : '';
    const medications = medicationsString.split(',').map(med => med.trim()).filter(med => med.length > 0);
    
    this.patient = interactionData?.patient;
    // Store the original medications string
    this.medicationsString = medicationsString;
    
    // Ensure genInteractions is always an array
    this.rawInteractions = Array.isArray(genInteractions) ? genInteractions : [];
    this.interactions = this.rawInteractions.map(interaction => ({
      description: interaction.interaction,
      severity: this.normalizeSeverity(interaction.severity),
      recommendationGeneral: interaction.recommendation_general,
      recommendationPatientSpecific: interaction.recommendation_patient_specific,
      drugs: medications // Use the medications from the server response
    }));
    this.severity = this.getHighestSeverity();
    this.uuid = interactionData && interactionData.uuid ? interactionData.uuid : undefined;
    this.completed_at = interactionData && interactionData.completed_at ? interactionData.completed_at : null;
    this.problem = interactionData?.problem;
  }

  normalizeSeverity(severity) {
    const s = (severity || '').toLowerCase();
    if (s === 'mayor' || s === 'major') return 'major';
    if (s === 'moderada' || s === 'moderate') return 'moderate';
    if (s === 'menor' || s === 'minor') return 'minor';
    return 'none';
  }

  getSeverity() {
    return this.severity;
  }

  getPatient() {
    return this.patient ? new PatientPresenter(this.patient) : null;
  }

  getInteractions() {
    return this.interactions;
  }

  hasInteractions() {
    return this.interactions && this.interactions.length > 0;
  }

  getMajorInteractions() {
    return this.interactions.filter(interaction => interaction.severity === 'major');
  }

  getModerateInteractions() {
    return this.interactions.filter(interaction => interaction.severity === 'moderate');
  }

  getMinorInteractions() {
    return this.interactions.filter(interaction => interaction.severity === 'minor');
  }

  hasMajorInteractions() {
    return this.getMajorInteractions().length > 0;
  }

  hasModerateInteractions() {
    return this.getModerateInteractions().length > 0;
  }

  hasMinorInteractions() {
    return this.getMinorInteractions().length > 0;
  }

  getHighestSeverity() {
    const severities = this.interactions.map(i => i.severity);
    if (severities.includes('major')) return 'major';
    if (severities.includes('moderate')) return 'moderate';
    if (severities.includes('minor')) return 'minor';
    return 'none';
  }

  getInteractionCount() {
    return this.interactions.length;
  }

  getInteractionCountBySeverity(severity) {
    return this.interactions.filter(interaction => interaction.severity === severity).length;
  }

  getUniqueDrugs() {
    const allDrugs = this.interactions.flatMap(interaction => interaction.drugs || []);
    return [...new Set(allDrugs)];
  }

  getInteractionsForDrug(drugName) {
    return this.interactions.filter(interaction => 
      interaction.drugs && interaction.drugs.some(drug => 
        drug.toLowerCase().includes(drugName.toLowerCase())
      )
    );
  }

  isSevere() {
    return this.getHighestSeverity() === 'major';
  }

  isModerate() {
    return this.getHighestSeverity() === 'moderate';
  }

  isMinor() {
    return this.getHighestSeverity() === 'minor';
  }

  hasNoInteractions() {
    return this.severity === 'none' || this.interactions.length === 0;
  }

  isReady() {
    // Check if the backend processing is complete
    // The backend response has a completed_at field that indicates when processing is done
    // If completed_at is not null, the processing is complete (even if no interactions found)
    return this.rawInteractions !== null && this.rawInteractions !== undefined && this.completed_at !== null;
  }

  isErrored() {
    return this.problem !== null;
  }

  getUUID() {
    return this.uuid;
  }

  getMedications() {
    return this.medicationsString;
  }
}

export default DrugInteractionsPresenter; 